import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  BarChart3,
  ChevronDown,
  ChevronRight,
  Loader2,
  RotateCcw,
  Truck,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { containerReportAPI } from "../../services/api";
import { withRoadDistances } from "../../utils/reportRoadDistances";
import { CONTAINER_LINE_STATUS_STYLES } from "../../constants/containerOrderConstants";
import {
  Card,
  DailyColumns,
  HeroFigure,
  InlineBar,
  MiniStackedBar,
  RankedBars,
  StackedBar,
  StatTile,
  VIZ,
  type Segment,
} from "../../components/reports/TruckReportCharts";
import type {
  TruckOption,
  TruckReport,
  TruckReportCounts,
  TruckReportLine,
  TruckReportRow,
} from "../../types/containerReports";

/**
 * ── Container work by truck, as a dashboard ──────────────────────────────────
 *
 * One filter row scopes everything below it — the date range and the truck
 * search both re-count the headline figures, the charts and the table, so the
 * numbers on screen always agree with each other.
 *
 *   · the headline: kilometres, with how they were measured
 *   · four stat tiles: containers, active trucks, truck-days, slots
 *   · containers worked per day across the range
 *   · the breakdown: progress, full/empty, shipping lines
 *   · the trucks, ranked, with the containers behind each on expand
 *
 * The server does the counting per truck (controllers/containerReportController.js);
 * the page only re-sums what the search leaves and draws it.
 */

type Preset = "thisWeek" | "lastWeek" | "thisMonth" | "lastMonth";
type SortKey = "km" | "containers" | "plate";

/** A local calendar day as `YYYY-MM-DD` — never via ISO, which shifts the day. */
const dayKey = (date: Date) =>
  [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, "0"),
    String(date.getDate()).padStart(2, "0"),
  ].join("-");

const parseDay = (key: string) => new Date(`${key}T00:00:00`);

function presetRange(preset: Preset, today = new Date()) {
  const at = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  if (preset === "thisWeek" || preset === "lastWeek") {
    const monday = new Date(at);
    monday.setDate(at.getDate() - ((at.getDay() + 6) % 7) - (preset === "lastWeek" ? 7 : 0));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: dayKey(monday), to: dayKey(sunday) };
  }
  const offset = preset === "lastMonth" ? -1 : 0;
  const first = new Date(at.getFullYear(), at.getMonth() + offset, 1);
  const last = new Date(at.getFullYear(), at.getMonth() + offset + 1, 0);
  return { from: dayKey(first), to: dayKey(last) };
}

const PRESETS: Preset[] = ["thisWeek", "lastWeek", "thisMonth", "lastMonth"];

/** Past this many days the daily chart groups by week — 90 columns are not readable. */
const DAILY_LIMIT = 62;

/** Shipping lines shown before the rest fold into "Other". */
const TOP_LINES = 5;

const COUNT_KEYS: (keyof TruckReportCounts)[] = [
  "containers",
  "days",
  "slots",
  "km",
  "kmIncomplete",
  "completed",
  "inProgress",
  "notStarted",
  "full",
  "empty",
  "unstated",
  "stops",
  "skippedStops",
];

export default function TruckReportPage() {
  const { t, i18n } = useTranslation("containerOrders");
  const navigate = useNavigate();

  const [range, setRange] = useState(() => presetRange("thisMonth"));
  const [report, setReport] = useState<TruckReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  /** The truck picked in the dropdown, or "" for every truck. */
  const [truckId, setTruckId] = useState("");
  const [truckOptions, setTruckOptions] = useState<TruckOption[]>([]);

  // The fleet does not change with the date range, so it is asked for once.
  useEffect(() => {
    containerReportAPI
      .truckOptions()
      .then((res) => setTruckOptions(res.data.data.trucks))
      .catch(() => setTruckOptions([]));
  }, []);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: "km", desc: true });

  /** Only the latest request may write — a quick change of range must not be overwritten by a slower, older answer. */
  const seq = useRef(0);

  const load = useCallback(async () => {
    if (!range.from || !range.to || range.to < range.from) return;
    const mine = ++seq.current;
    setLoading(true);
    try {
      const res = await containerReportAPI.trucks(range);
      if (mine !== seq.current) return;

      /*
       * Measured before it is shown, not after.
       *
       * Where this deployment has no server-side Maps key the server can only
       * estimate, while the container's own page — which does have a key —
       * measures the roads. Two pages disagreeing about one container is what
       * this answers. A no-op where the server measured roads itself.
       *
       * Awaited inside the existing loading state deliberately: every figure on
       * this page is a sum, and letting measurements land one at a time would
       * have the totals and the bars climb while somebody reads them.
       */
      const measured = await withRoadDistances(res.data.data);
      if (mine !== seq.current) return;

      setReport(measured);
      setError(null);
    } catch (err) {
      if (mine !== seq.current) return;
      setError(
        (err as { response?: { data?: { message?: string } } }).response?.data?.message ??
          t("truckReport.errors.load"),
      );
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [range, t]);

  useEffect(() => {
    void load();
  }, [load]);

  // ── Formatting ───────────────────────────────────────────────────────────
  const number = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 1 }),
    [i18n.language],
  );
  const whole = useMemo(
    () => new Intl.NumberFormat(i18n.language, { maximumFractionDigits: 0 }),
    [i18n.language],
  );
  const estimate = report?.distanceSource === "straight";
  const km = useCallback(
    (value: number | null) => (value === null ? "—" : `${estimate ? "≈ " : ""}${number.format(value)}`),
    [estimate, number],
  );
  const fmtDay = useCallback(
    (key: string, options: Intl.DateTimeFormatOptions) =>
      parseDay(key).toLocaleDateString(i18n.language, options),
    [i18n.language],
  );

  // ── The slice everything below is drawn from ─────────────────────────────
  const trucks = useMemo(() => {
    if (!report) return [];
    // Picked from the dropdown, filtered here: the report already holds every
    // truck for the range, so changing trucks needs no new request.
    const matched = truckId ? report.trucks.filter((truck) => truck.id === truckId) : report.trucks;
    const dir = sort.desc ? -1 : 1;
    const byName = (a: TruckReportRow, b: TruckReportRow) =>
      a.name.localeCompare(b.name, undefined, { numeric: true });
    return [...matched].sort((a, b) =>
      sort.key === "plate"
        ? dir * byName(a, b)
        : dir * (a[sort.key] - b[sort.key]) || byName(a, b),
    );
  }, [report, truckId, sort]);

  const totals = useMemo(() => {
    const sum = Object.fromEntries(COUNT_KEYS.map((k) => [k, 0])) as unknown as TruckReportCounts;
    for (const truck of trucks) for (const k of COUNT_KEYS) sum[k] += truck[k];
    return sum;
  }, [trucks]);

  const lines = useMemo(() => trucks.flatMap((truck) => truck.lines), [trucks]);

  /** Containers worked per day — a container on two days counts on both. */
  const daily = useMemo(() => {
    if (!report) return { data: [], weekly: false };
    const days: string[] = [];
    for (let at = parseDay(report.from); dayKey(at) <= report.to; at.setDate(at.getDate() + 1)) {
      days.push(dayKey(at));
    }
    const weekly = days.length > DAILY_LIMIT;
    const bucketOf = (key: string) => {
      if (!weekly) return key;
      const at = parseDay(key);
      at.setDate(at.getDate() - ((at.getDay() + 6) % 7));
      return dayKey(at);
    };

    const counts = new Map<string, number>();
    for (const key of days) counts.set(bucketOf(key), 0);
    for (const line of lines) {
      for (const key of line.days) {
        const bucket = bucketOf(key);
        if (counts.has(bucket)) counts.set(bucket, counts.get(bucket)! + 1);
      }
    }

    const short = days.length <= 14;
    const data = [...counts.entries()].map(([key, value]) => ({
      key,
      value,
      label: weekly
        ? fmtDay(key, { day: "numeric", month: "short" })
        : short
          ? fmtDay(key, { weekday: "short", day: "numeric" })
          : fmtDay(key, { day: "numeric" }),
      tooltipLabel: weekly
        ? t("truckReport.dashboard.weekOf", { day: fmtDay(key, { day: "numeric", month: "short" }) })
        : fmtDay(key, { weekday: "long", day: "numeric", month: "short" }),
    }));
    return { data, weekly };
  }, [report, lines, fmtDay, t]);

  const busiestDay = useMemo(
    () => daily.data.reduce<(typeof daily.data)[number] | null>((best, d) => (!best || d.value > best.value ? d : best), null),
    [daily],
  );

  const shippingLines = useMemo(() => {
    const byLine = new Map<string, { label: string; value: number }>();
    let unset = 0;
    for (const line of lines) {
      const name = line.shippingLine?.trim();
      if (!name) {
        unset += 1;
        continue;
      }
      const key = name.toLowerCase();
      const entry = byLine.get(key) ?? { label: name, value: 0 };
      entry.value += 1;
      byLine.set(key, entry);
    }
    const ranked = [...byLine.entries()]
      .map(([key, v]) => ({ key, ...v }))
      .sort((a, b) => b.value - a.value || a.label.localeCompare(b.label));
    const rows: { key: string; label: string; value: number; muted?: boolean }[] = ranked.slice(0, TOP_LINES);
    const other = ranked.slice(TOP_LINES).reduce((s, r) => s + r.value, 0);
    if (other) rows.push({ key: "__other", label: t("truckReport.dashboard.other"), value: other, muted: true });
    if (unset) rows.push({ key: "__unset", label: t("truckReport.dashboard.noLine"), value: unset, muted: true });
    return rows;
  }, [lines, t]);

  const progressSegments = (c: Pick<TruckReportCounts, "completed" | "inProgress" | "notStarted">): Segment[] => [
    { key: "completed", label: t("truckReport.dashboard.progress.completed"), value: c.completed, color: VIZ.progress.completed },
    { key: "inProgress", label: t("truckReport.dashboard.progress.inProgress"), value: c.inProgress, color: VIZ.progress.inProgress },
    { key: "notStarted", label: t("truckReport.dashboard.progress.notStarted"), value: c.notStarted, color: VIZ.progress.notStarted },
  ];

  const loadSegments: Segment[] = [
    { key: "full", label: t("loadStatus.full"), value: totals.full, color: VIZ.series1 },
    { key: "empty", label: t("loadStatus.empty"), value: totals.empty, color: VIZ.series2 },
    { key: "unstated", label: t("loadStatus.notStated"), value: totals.unstated, color: VIZ.neutral },
  ];

  const maxKm = Math.max(0, ...trucks.map((truck) => truck.km));
  const completion = totals.containers ? Math.round((totals.completed / totals.containers) * 100) : 0;

  const activePreset = PRESETS.find((p) => {
    const r = presetRange(p);
    return r.from === range.from && r.to === range.to;
  });

  const rangeLabel = report
    ? `${fmtDay(report.from, { day: "numeric", month: "short" })} – ${fmtDay(report.to, { day: "numeric", month: "short", year: "numeric" })}`
    : "";

  const toggle = (id: string) =>
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const sortBy = (key: SortKey) =>
    setSort((current) =>
      current.key === key ? { key, desc: !current.desc } : { key, desc: key !== "plate" },
    );

  const sortHeader = (label: string, sortKey: SortKey, align: "left" | "right" = "left") => (
    <th
      aria-sort={sort.key === sortKey ? (sort.desc ? "descending" : "ascending") : "none"}
      className={`px-3 py-2.5 font-semibold ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        type="button"
        onClick={() => sortBy(sortKey)}
        className={`inline-flex items-center gap-1 transition hover:text-[#0b0b0b] ${sort.key === sortKey ? "text-[#0b0b0b]" : ""}`}
      >
        {label}
        {sort.key === sortKey &&
          (sort.desc ? <ArrowDown className="h-3 w-3" /> : <ArrowUp className="h-3 w-3" />)}
      </button>
    </th>
  );

  return (
    <div className="mx-auto w-full max-w-[1400px] p-3 sm:p-5">
      {/* ── Header ───────────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap items-end justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5 shrink-0 text-primary" />
            <h1 className="truncate text-[18px] font-bold text-ink">{t("truckReport.title")}</h1>
            {loading && report && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-gray-400" />}
          </div>
          {rangeLabel && <p className="mt-0.5 text-[12.5px] text-[#52514e]">{rangeLabel}</p>}
        </div>
      </div>

      {/* ── One filter row, scoping everything below ─────────────────────── */}
      <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-black/10 bg-white p-2">
        <div className="flex overflow-hidden rounded-lg bg-[#f0efec] p-0.5">
          {PRESETS.map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => setRange(presetRange(preset))}
              className={`rounded-md px-3 py-1.5 text-[12px] font-semibold transition ${
                activePreset === preset
                  ? "bg-white text-[#0b0b0b] shadow-sm"
                  : "text-[#52514e] hover:text-[#0b0b0b]"
              }`}
            >
              {t(`truckReport.presets.${preset}`)}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-1.5 text-[12px] text-[#898781]">
          <input
            type="date"
            value={range.from}
            max={range.to}
            onChange={(e) => e.target.value && setRange((r) => ({ ...r, from: e.target.value }))}
            aria-label={t("truckReport.from")}
            className="rounded-lg border border-black/10 px-2 py-1.5 text-[12px] text-[#0b0b0b] focus:border-secondary-500 focus:outline-none"
          />
          <span>–</span>
          <input
            type="date"
            value={range.to}
            min={range.from}
            onChange={(e) => e.target.value && setRange((r) => ({ ...r, to: e.target.value }))}
            aria-label={t("truckReport.to")}
            className="rounded-lg border border-black/10 px-2 py-1.5 text-[12px] text-[#0b0b0b] focus:border-secondary-500 focus:outline-none"
          />
        </div>

        <div className="relative w-full sm:ml-auto sm:w-[240px]">
          <Truck className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#898781]" />
          <select
            value={truckId}
            onChange={(e) => setTruckId(e.target.value)}
            aria-label={t("truckReport.columns.truck")}
            className={`w-full appearance-none rounded-lg border py-1.5 pl-8 pr-7 text-[12.5px] focus:border-secondary-500 focus:outline-none focus:ring-1 focus:ring-secondary-500 ${
              truckId ? "border-secondary-400 bg-secondary-50 font-semibold text-[#0b0b0b]" : "border-black/10 bg-white text-[#52514e]"
            }`}
          >
            <option value="">{t("truckReport.allTrucks")}</option>
            {truckOptions.map((truck) => (
              <option key={truck.id} value={truck.id}>
                {truck.model ? `${truck.name} — ${truck.model}` : truck.name}
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#898781]" />
        </div>

        <button
          type="button"
          onClick={() => void load()}
          className="rounded-lg border border-black/10 p-1.5 text-[#52514e] transition hover:bg-[#f0efec]"
          aria-label={t("truckReport.refresh")}
        >
          <RotateCcw className="h-4 w-4" />
        </button>
      </div>

      {loading && !report ? (
        <div className="flex items-center justify-center gap-2 py-24 text-[#52514e]">
          <Loader2 className="h-5 w-5 animate-spin" />
          <span className="text-[13px]">{t("truckReport.loading")}</span>
        </div>
      ) : error && !report ? (
        <p className="py-24 text-center text-[13px] text-red-600">{error}</p>
      ) : report ? (
        // Refetch keeps the frame: the previous numbers stay, dimmed, until the new ones land.
        <div className={`space-y-4 transition-opacity ${loading ? "opacity-60" : ""}`}>
          {error && <p className="text-[12.5px] text-red-600">{error}</p>}

          {/* ── Headline ────────────────────────────────────────────────── */}
          <div className="grid gap-4 lg:grid-cols-3">
            <HeroFigure
              label={t("truckReport.dashboard.totalKm")}
              value={km(totals.km)}
              unit="km"
              caption={estimate ? t("truckReport.dashboard.sourceStraight") : t("truckReport.dashboard.sourceRoad")}
              warning={
                totals.kmIncomplete > 0 ? t("truckReport.kmIncomplete", { count: totals.kmIncomplete }) : null
              }
              footer={
                <dl className="grid grid-cols-2 gap-3">
                  <div>
                    <dt className="text-[11px] text-[#898781]">{t("truckReport.dashboard.kmPerContainer")}</dt>
                    <dd className="text-[16px] font-semibold text-[#0b0b0b]">
                      {totals.containers ? km(totals.km / totals.containers) : "—"}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-[11px] text-[#898781]">{t("truckReport.dashboard.kmPerTruck")}</dt>
                    <dd className="text-[16px] font-semibold text-[#0b0b0b]">
                      {trucks.length ? km(totals.km / trucks.length) : "—"}
                    </dd>
                  </div>
                </dl>
              }
            />

            <div className="grid min-w-0 grid-cols-2 gap-3 sm:gap-4 lg:col-span-2">
              <StatTile
                label={t("truckReport.summary.containers")}
                value={whole.format(totals.containers)}
                hint={t("truckReport.dashboard.completionHint", { percent: completion })}
              />
              <StatTile
                label={t("truckReport.dashboard.activeTrucks")}
                value={whole.format(trucks.length)}
                hint={
                  trucks.length
                    ? t("truckReport.dashboard.perTruckHint", { value: number.format(totals.containers / trucks.length) })
                    : undefined
                }
              />
              <StatTile
                label={t("truckReport.summary.truckDays")}
                value={whole.format(totals.days)}
                hint={
                  busiestDay && busiestDay.value > 0
                    ? t("truckReport.dashboard.busiestHint", { day: busiestDay.tooltipLabel, count: busiestDay.value })
                    : undefined
                }
              />
              <StatTile
                label={t("truckReport.summary.slots")}
                value={whole.format(totals.slots)}
                hint={
                  totals.days
                    ? t("truckReport.dashboard.slotsHint", { value: number.format(totals.slots / totals.days) })
                    : undefined
                }
              />
            </div>
          </div>

          {/* ── Activity and breakdown ─────────────────────────────────── */}
          <div className="grid gap-4 lg:grid-cols-3">
            <Card
              className="lg:col-span-2"
              title={daily.weekly ? t("truckReport.dashboard.perWeek") : t("truckReport.dashboard.perDay")}
              subtitle={t("truckReport.dashboard.perDayHint")}
            >
              {lines.length ? (
                <DailyColumns
                  data={daily.data}
                  valueLabel={t("truckReport.summary.containers")}
                  format={(v) => whole.format(v)}
                />
              ) : (
                <EmptyState text={report.trucks.length ? t("truckReport.noTruckWork") : t("truckReport.empty")} />
              )}
            </Card>

            <Card title={t("truckReport.dashboard.breakdown")}>
              <div className="space-y-5">
                <div>
                  <h3 className="mb-2 text-[11.5px] font-semibold text-[#52514e]">
                    {t("truckReport.columns.progress")}
                  </h3>
                  <StackedBar segments={progressSegments(totals)} format={(v) => whole.format(v)} />
                </div>
                <div>
                  <h3 className="mb-2 text-[11.5px] font-semibold text-[#52514e]">
                    {t("truckReport.lineColumns.load")}
                  </h3>
                  <StackedBar segments={loadSegments} format={(v) => whole.format(v)} />
                </div>
                <div>
                  <h3 className="mb-2 text-[11.5px] font-semibold text-[#52514e]">
                    {t("truckReport.lineColumns.shippingLine")}
                  </h3>
                  {shippingLines.length ? (
                    <RankedBars rows={shippingLines} format={(v) => whole.format(v)} />
                  ) : (
                    <p className="text-[11.5px] text-[#898781]">—</p>
                  )}
                </div>
              </div>
            </Card>
          </div>

          {/* ── The trucks ──────────────────────────────────────────────── */}
          <Card
            title={t("truckReport.dashboard.trucks")}
            subtitle={t("truckReport.dashboard.trucksHint")}
            flush
          >
            {trucks.length === 0 ? (
              <EmptyState text={report.trucks.length ? t("truckReport.noTruckWork") : t("truckReport.empty")} />
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] border-collapse text-[12.5px]">
                  <thead>
                    <tr className="border-y border-[#e1e0d9] bg-[#fcfcfb] text-[11px] text-[#898781]">
                      <th className="w-10 px-3 py-2.5" />
                      {sortHeader(t("truckReport.columns.truck"), "plate")}
                      {sortHeader(t("truckReport.columns.containers"), "containers", "right")}
                      {sortHeader(t("truckReport.columns.km"), "km")}
                      <th className="px-3 py-2.5 text-left font-semibold">{t("truckReport.columns.progress")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {trucks.map((truck) => (
                      <Fragment key={truck.id}>
                        <TruckRow
                          truck={truck}
                          open={expanded.has(truck.id)}
                          onToggle={() => toggle(truck.id)}
                          km={km}
                          maxKm={maxKm}
                          whole={whole}
                          segments={progressSegments(truck)}
                        />
                        {expanded.has(truck.id) && (
                          <tr>
                            <td colSpan={5} className="border-b border-[#e1e0d9] bg-[#fcfcfb] px-4 py-3">
                              <div className="overflow-x-auto">
                                <LinesTable
                                  lines={truck.lines}
                                  km={km}
                                  shortDay={(key) => fmtDay(key, { day: "numeric", month: "short" })}
                                  onOpen={(line) =>
                                    navigate(`/container-orders/${line.orderId}/container/${line.id}`)
                                  }
                                />
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                  {trucks.length > 1 && (
                    <tfoot>
                      <tr className="bg-[#fcfcfb] font-semibold text-[#0b0b0b]">
                        <td className="px-3 py-2.5" />
                        <td className="px-3 py-2.5">{t("truckReport.total")}</td>
                        <td className="px-3 py-2.5 text-right tabular-nums">{whole.format(totals.containers)}</td>
                        <td className="px-3 py-2.5">
                          <div className="min-w-[180px] whitespace-nowrap text-right tabular-nums">{km(totals.km)}</div>
                        </td>
                        <td className="px-3 py-2.5 text-[11.5px] font-normal text-[#52514e]">
                          {t("truckReport.dashboard.completionHint", { percent: completion })}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}
          </Card>
        </div>
      ) : null}
    </div>
  );
}

function EmptyState({ text }: { text: string }) {
  return <p className="py-14 text-center text-[13px] text-[#898781]">{text}</p>;
}

type KmFormat = (value: number | null) => string;

function TruckRow({
  truck,
  open,
  onToggle,
  km,
  maxKm,
  whole,
  segments,
}: {
  truck: TruckReportRow;
  open: boolean;
  onToggle: () => void;
  km: KmFormat;
  maxKm: number;
  whole: Intl.NumberFormat;
  segments: Segment[];
}) {
  const { t } = useTranslation("containerOrders");
  return (
    <tr
      onClick={onToggle}
      aria-expanded={open}
      className={`cursor-pointer border-b border-[#e1e0d9] transition hover:bg-[#fcfcfb] ${open ? "bg-[#fcfcfb]" : ""}`}
    >
      <td className="px-3 py-3 text-[#898781]">
        {open ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
      </td>
      <td className="px-3 py-3">
        <div className="font-mono text-[13px] font-bold text-[#0b0b0b]">{truck.name}</div>
        {(truck.name !== truck.plate || truck.model) && (
          <div className="text-[11px] text-[#898781]">
            {[truck.name !== truck.plate ? truck.plate : "", truck.model]
              .filter(Boolean)
              .join(" · ")}
          </div>
        )}
      </td>
      <td className="px-3 py-3 text-right text-[13px] font-semibold tabular-nums text-[#0b0b0b]">
        {whole.format(truck.containers)}
      </td>
      <td className="px-3 py-3">
        <InlineBar value={truck.km} max={maxKm}>
          <span className="inline-flex items-center justify-end gap-1 text-[13px] font-semibold tabular-nums text-[#0b0b0b]">
            {km(truck.km)}
            {truck.kmIncomplete > 0 && (
              <span title={t("truckReport.kmIncomplete", { count: truck.kmIncomplete })} className="text-amber-600">
                <AlertTriangle className="h-3.5 w-3.5" aria-label={t("truckReport.kmIncomplete", { count: truck.kmIncomplete })} />
              </span>
            )}
          </span>
        </InlineBar>
      </td>
      <td className="px-3 py-3">
        <div className="w-full max-w-[260px]">
          <MiniStackedBar segments={segments} />
          <div className="mt-1 flex flex-wrap gap-x-2.5 text-[10.5px] text-[#52514e]">
            {segments
              .filter((s) => s.value > 0)
              .map((s) => (
                <span key={s.key} className="inline-flex items-center gap-1">
                  <span className="h-2 w-2 rounded-sm" style={{ background: s.color }} aria-hidden />
                  {s.label} <span className="font-semibold tabular-nums text-[#0b0b0b]">{s.value}</span>
                </span>
              ))}
          </div>
        </div>
      </td>
    </tr>
  );
}

function LinesTable({
  lines,
  km,
  shortDay,
  onOpen,
}: {
  lines: TruckReportLine[];
  km: KmFormat;
  shortDay: (key: string) => string;
  onOpen: (line: TruckReportLine) => void;
}) {
  const { t } = useTranslation("containerOrders");
  return (
    <table className="w-full min-w-[900px] border-collapse overflow-hidden rounded-lg bg-white text-[12px] ring-1 ring-[#e1e0d9]">
      <thead>
        <tr className="border-b border-[#e1e0d9] text-left text-[10.5px] font-semibold text-[#898781]">
          <th className="px-3 py-2">{t("truckReport.lineColumns.container")}</th>
          <th className="px-3 py-2">{t("truckReport.lineColumns.client")}</th>
          <th className="px-3 py-2">{t("truckReport.lineColumns.shippingLine")}</th>
          <th className="px-3 py-2">{t("truckReport.lineColumns.load")}</th>
          <th className="px-3 py-2">{t("truckReport.lineColumns.days")}</th>
          <th className="px-3 py-2 text-right">{t("truckReport.lineColumns.slots")}</th>
          <th className="px-3 py-2">{t("truckReport.lineColumns.route")}</th>
          <th className="px-3 py-2 text-right">{t("truckReport.lineColumns.km")}</th>
          <th className="px-3 py-2">{t("truckReport.lineColumns.status")}</th>
        </tr>
      </thead>
      <tbody>
        {lines.map((line) => (
          <tr key={line.id} className="border-b border-[#f0efec] align-top last:border-b-0">
            <td className="px-3 py-2">
              <button
                type="button"
                onClick={() => onOpen(line)}
                className="font-mono font-semibold text-secondary-700 hover:underline"
              >
                {line.containerNumber || line.lineNumber || "—"}
              </button>
              <div className="font-mono text-[10.5px] text-[#898781]">
                {[line.lineNumber, line.containerType].filter(Boolean).join(" · ")}
              </div>
            </td>
            <td className="px-3 py-2 text-[#0b0b0b]">{line.client || "—"}</td>
            <td className="px-3 py-2 text-[#0b0b0b]">{line.shippingLine || "—"}</td>
            <td className="px-3 py-2">
              <span className="inline-flex items-center gap-1.5 text-[#0b0b0b]">
                <span
                  className="h-2 w-2 rounded-sm"
                  style={{
                    background:
                      line.loadStatus === "full" ? VIZ.series1 : line.loadStatus === "empty" ? VIZ.series2 : VIZ.neutral,
                  }}
                  aria-hidden
                />
                {line.loadStatus ? t(`loadStatus.${line.loadStatus}`) : t("loadStatus.notStated")}
              </span>
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-[#52514e]">{line.days.map(shortDay).join(", ")}</td>
            <td className="px-3 py-2 text-right tabular-nums">{line.slots || "—"}</td>
            <td className="px-3 py-2 text-[11px] text-[#52514e]">
              {line.route.join(" → ") || "—"}
              {line.skippedStops > 0 && (
                <span className="ml-1 text-[#898781]">
                  ({t("truckReport.notUsed", { count: line.skippedStops })})
                </span>
              )}
            </td>
            <td className="whitespace-nowrap px-3 py-2 text-right font-semibold tabular-nums">
              {km(line.km)}
              {line.missingStops > 0 && (
                <span
                  title={t("truckReport.missingStops", { count: line.missingStops })}
                  className="ml-1 inline-flex align-middle text-amber-600"
                >
                  <AlertTriangle className="h-3 w-3" />
                </span>
              )}
            </td>
            <td className="px-3 py-2">
              <span
                className={`inline-block whitespace-nowrap rounded px-1.5 py-0.5 text-[10.5px] font-semibold ${
                  CONTAINER_LINE_STATUS_STYLES[line.status] ?? CONTAINER_LINE_STATUS_STYLES.planned
                }`}
              >
                {t(`linesBlock.statuses.${line.status}`, { defaultValue: line.status })}
              </span>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
